import type { CategoriaDeStatus } from '@/features/tickets/types';

/**
 * O formato do que a API de Change Enablement devolve.
 *
 * Declarado aqui, e não importado de `@prisma/client` nem de `@central/*`,
 * porque o que atravessa a rede é JSON: `startsAt` é string ISO e não `Date`,
 * e `riskAnswers` é objeto solto e não `Prisma.JsonValue`. Copiar o tipo do
 * banco daria a impressão errada de que os dois são a mesma coisa, e o erro só
 * apareceria em execução.
 *
 * Nada aqui é inventado: cada campo existe em alguma resposta de
 * `apps/api/src/itsm/changes/changes.controller.ts`.
 */

// ── vocabulário ────────────────────────────────────────────────────────────

export const TIPOS_DE_MUDANCA = ['STANDARD', 'NORMAL', 'EMERGENCY'] as const;
export type TipoDeMudanca = (typeof TIPOS_DE_MUDANCA)[number];

/** Documento 09 §6 — os termos ITIL em português que a interface usa. */
export const ROTULO_DE_TIPO_DE_MUDANCA: Record<TipoDeMudanca, string> = {
  STANDARD: 'Mudança padrão',
  NORMAL: 'Mudança normal',
  EMERGENCY: 'Mudança emergencial',
};

export const RESUMO_DE_TIPO_DE_MUDANCA: Record<TipoDeMudanca, string> = {
  STANDARD:
    'Pré-aprovada: o risco foi avaliado uma vez, no modelo, e o fluxo não tem estado de aprovação. Continua precisando de janela.',
  NORMAL:
    'Passa pelo Comitê de Mudanças (CAB) ou pelo gestor antes de ser agendada — a política é escolhida pelo nível de risco.',
  EMERGENCY:
    'É autorizada agora e ratificada depois pelo Comitê Emergencial (ECAB). A aprovação vem no fim, não no começo.',
};

export const NIVEIS_DE_RISCO = ['LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH'] as const;
export type NivelDeRisco = (typeof NIVEIS_DE_RISCO)[number];

export const ROTULO_DE_RISCO: Record<NivelDeRisco, string> = {
  LOW: 'Baixo',
  MEDIUM: 'Médio',
  HIGH: 'Alto',
  VERY_HIGH: 'Muito alto',
};

export const DESFECHOS = [
  'SUCCESSFUL',
  'SUCCESSFUL_WITH_ISSUES',
  'FAILED',
  'ROLLED_BACK',
] as const;
export type Desfecho = (typeof DESFECHOS)[number];

export const ROTULO_DE_DESFECHO: Record<Desfecho, string> = {
  SUCCESSFUL: 'Bem-sucedida',
  SUCCESSFUL_WITH_ISSUES: 'Bem-sucedida com ressalvas',
  FAILED: 'Falhou',
  ROLLED_BACK: 'Revertida',
};

export const IMPACTOS_NO_IC = ['ADD', 'MODIFY', 'RESTART', 'REPLACE', 'REMOVE'] as const;
export type ImpactoNoIc = (typeof IMPACTOS_NO_IC)[number];

export const ROTULO_DE_IMPACTO: Record<ImpactoNoIc, string> = {
  ADD: 'Acrescenta',
  MODIFY: 'Altera',
  RESTART: 'Reinicia',
  REPLACE: 'Substitui',
  REMOVE: 'Remove',
};

export const SITUACOES_DE_JANELA = [
  'PLANNED',
  'CONFIRMED',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
] as const;
export type SituacaoDeJanela = (typeof SITUACOES_DE_JANELA)[number];

export const ROTULO_DE_JANELA: Record<SituacaoDeJanela, string> = {
  PLANNED: 'Planejada',
  CONFIRMED: 'Confirmada',
  IN_PROGRESS: 'Em execução',
  COMPLETED: 'Concluída',
  CANCELLED: 'Cancelada',
};

export type Enforcement = 'BLOCKING' | 'ADVISORY';

export const ROTULO_DE_ENFORCEMENT: Record<Enforcement, string> = {
  BLOCKING: 'Bloqueia o agendamento',
  ADVISORY: 'Apenas informativo',
};

export type SituacaoDeExcecao = 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'WITHDRAWN';

export const ROTULO_DE_EXCECAO: Record<SituacaoDeExcecao, string> = {
  REQUESTED: 'Aguardando decisão',
  APPROVED: 'Aprovada',
  REJECTED: 'Recusada',
  WITHDRAWN: 'Retirada',
};

// ── conflito ───────────────────────────────────────────────────────────────

export type TipoDeConflito = 'IC_COMPARTILHADO' | 'SERVICO_COMPARTILHADO' | 'CONGELAMENTO';
export type GravidadeDeConflito = 'BLOQUEANTE' | 'ATENCAO';

export const ROTULO_DE_TIPO_DE_CONFLITO: Record<TipoDeConflito, string> = {
  IC_COMPARTILHADO: 'Mesmo item de configuração',
  SERVICO_COMPARTILHADO: 'Mesmo serviço derrubado',
  CONGELAMENTO: 'Congelamento',
};

/** Intervalo em milissegundos da época, como o domínio puro o devolve. */
export interface Intervalo {
  inicio: number;
  fim: number;
}

/**
 * Uma colisão detectada — `changes/domain/conflitos.ts`.
 *
 * O servidor a devolve como **informação**, não como erro: só congelamento
 * `BLOCKING` sem exceção aprovada vem com gravidade `BLOQUEANTE`, e é a tela
 * que transforma o resto em decisão do Comitê de Mudanças.
 */
export interface Conflito {
  tipo: TipoDeConflito;
  gravidade: GravidadeDeConflito;
  comTicketId?: string;
  comBlackoutId?: string;
  /** Ids dos recursos compartilhados que produziram a colisão. */
  recursos: string[];
  sobreposicao: Intervalo;
  mensagem: string;
}

// ── janela e congelamento ──────────────────────────────────────────────────

export interface JanelaDeMudanca {
  id: string;
  name: string;
  description: string | null;
  startsAt: string;
  endsAt: string;
  status: SituacaoDeJanela;
  ownerTeamId: string | null;
  recurrenceNote: string | null;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** `GET /changes/windows` acrescenta a contagem do que está pendurado nela. */
export interface JanelaListada extends JanelaDeMudanca {
  _count: { changes: number; releases: number };
}

/** `GET /changes/windows/:id` traz quem está dentro. */
export interface JanelaDetalhada extends JanelaDeMudanca {
  changes: Array<{
    ticketId: string;
    changeType: TipoDeMudanca;
    riskLevel: NivelDeRisco;
    outcome: Desfecho | null;
    ticket: {
      id: string;
      number: string;
      subject: string;
      statusCategory: CategoriaDeStatus;
    };
  }>;
  releases: Array<{ id: string; code: string; name: string; status: string }>;
}

export interface Congelamento {
  id: string;
  name: string;
  reason: string;
  startsAt: string;
  endsAt: string;
  enforcement: Enforcement;
  scopeServiceIds: string[];
  scopeCiIds: string[];
  scopeOrganizationIds: string[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** Uma linha da Interrupção Projetada de Serviço. */
export interface InterrupcaoProjetada {
  serviceId: string;
  serviceName: string;
  windowId: string;
  windowName: string;
  startsAt: string;
  endsAt: string;
  expectedDowntimeMinutes: number | null;
  changeTicketId: string;
  changeNumber: string;
  changeSubject: string;
  windowStatus: SituacaoDeJanela;
}

/** `GET /changes/schedule` — as três coisas do calendário no mesmo período. */
export interface Calendario {
  from: string;
  to: string;
  windows: JanelaListada[];
  blackouts: Congelamento[];
  projectedOutages: InterrupcaoProjetada[];
}

// ── a mudança ──────────────────────────────────────────────────────────────

export interface ServicoAfetado {
  ticketId: string;
  serviceId: string;
  causesOutage: boolean;
  expectedDowntimeMinutes: number | null;
  note: string | null;
  createdAt: string;
  /** Só vem no `GET`; o `POST` de substituição devolve as linhas cruas. */
  service?: { id: string; name: string; key: string };
}

export interface IcAfetado {
  ticketId: string;
  ciId: string;
  impactKind: ImpactoNoIc;
  causesOutage: boolean;
  note: string | null;
  createdAt: string;
  ci?: { id: string; name: string; ciType: string };
}

export interface ExcecaoDeCongelamento {
  id: string;
  ticketId: string;
  blackoutPeriodId: string;
  justification: string;
  status: SituacaoDeExcecao;
  approvalRequestId: string | null;
  requestedById: string;
  requestedAt: string;
  decidedById: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  version: number;
  blackoutPeriod?: { id: string; name: string };
}

/** A linha de `ChangeDetail`, sem os relacionamentos. */
export interface DetalheDeMudanca {
  ticketId: string;
  changeType: TipoDeMudanca;
  justification: string | null;
  riskLevel: NivelDeRisco;
  riskScore: number | null;
  riskAnswers: { questionnaireVersion?: number; answers?: Record<string, string> } | null;
  implementationPlan: string | null;
  testPlan: string | null;
  rollbackPlan: string | null;
  implementationEvidence: string | null;
  plannedStart: string | null;
  plannedEnd: string | null;
  actualStart: string | null;
  actualEnd: string | null;
  changeWindowId: string | null;
  outcome: Desfecho | null;
  pirNote: string | null;
  pirDueAt: string | null;
  pirCompletedAt: string | null;
  pirById: string | null;
  detectedRetroactively: boolean;
  rollbackReason: string | null;
  updatedAt: string;
  version: number;
}

/** `GET /tickets/:id/change` — a mudança inteira, com a colisão já calculada. */
export interface Mudanca extends DetalheDeMudanca {
  ticket: {
    id: string;
    number: string;
    subject: string;
    statusCategory: CategoriaDeStatus;
    workflowVersionId: string | null;
    version: number;
  };
  affectedServices: ServicoAfetado[];
  affectedCis: IcAfetado[];
  changeWindow: JanelaDeMudanca | null;
  blackoutExceptions: ExcecaoDeCongelamento[];
  conflicts: Conflito[];
  blocking: Conflito[];
}

/** `GET /tickets/:id/change/schedule/preview` — o "e se?" sem gravar nada. */
export interface PreviaDeAgendamento {
  windowId: string;
  allowed: boolean;
  conflicts: Conflito[];
  blocking: Conflito[];
}

// ── questionário de risco ──────────────────────────────────────────────────

export interface OpcaoDeRisco {
  value: string;
  label: string;
  points: number;
}

export interface PerguntaDeRisco {
  key: string;
  label: string;
  options: OpcaoDeRisco[];
}

export interface QuestionarioDeRisco {
  version: number;
  questions: PerguntaDeRisco[];
  bands: Array<{ upTo: number; level: NivelDeRisco }>;
}

/** O que `POST /tickets/:id/change/risk` devolve junto com a linha gravada. */
export interface AvaliacaoDeRisco {
  questionnaireVersion: number;
  score: number;
  level: NivelDeRisco;
  unanswered: string[];
  unknown: string[];
}

export interface RespostaDeAvaliacao extends DetalheDeMudanca {
  assessment: AvaliacaoDeRisco;
}

// ── provisionamento dos fluxos ─────────────────────────────────────────────

export interface ResultadoDeProvisionamento {
  key: string;
  name: string;
  action: 'CRIADO' | 'MANTIDO';
  versionId?: string;
  detail?: string;
}

export interface Provisionamento {
  policies: ResultadoDeProvisionamento[];
  workflows: ResultadoDeProvisionamento[];
}

// ── catálogo, para os seletores de escopo ──────────────────────────────────

/**
 * Serviço de negócio, de `GET /catalog/services`.
 *
 * Só os campos que estas telas usam. A resposta traz a linha inteira do
 * catálogo, mas declarar trinta campos aqui daria a impressão de que a tela
 * depende deles.
 */
export interface ServicoDoCatalogo {
  id: string;
  key: string;
  name: string;
  criticality: string;
}

/** Componente de um serviço, de `GET /catalog/services/:id/components`. */
export interface ComponenteDeServico {
  serviceId: string;
  ciId: string;
  criticality: string;
  note: string | null;
  ci: { id: string; name: string; ciType: string; lifecycleStatus: string };
}
